"use client";

import { useEffect, useRef, useCallback, useState } from "react";

const CLOB_WS_URL = process.env.NEXT_PUBLIC_POLYMARKET_CLOB_WS_URL || "wss://ws-subscriptions-clob-v2.polymarket.com/ws/market";

interface OrderBookUpdate {
  market: string;
  asset_id: string;
  timestamp: string;
  hash: string;
  bids: Array<{ price: string; size: string }>;
  asks: Array<{ price: string; size: string }>;
}

type PriceCallback = (assetId: string, bestBid: number, bestAsk: number) => void;

export function useClobWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const subscribedTokensRef = useRef<Set<string>>(new Set());
  const callbacksRef = useRef<Map<string, PriceCallback>>(new Map());
  const [connected, setConnected] = useState(false);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(CLOB_WS_URL);

      ws.onopen = () => {
        setConnected(true);
        // Resubscribe to all tokens
        if (subscribedTokensRef.current.size > 0) {
          const tokens = Array.from(subscribedTokensRef.current);
          ws.send(
            JSON.stringify({
              assets_ids: tokens,
              type: "market",
              custom_feature_enabled: true,
            })
          );
        }
      };

      ws.onmessage = (event) => {
        try {
          const raw = event.data;
          // Ignore non-JSON messages (PONG, INVALID OPERATION, etc.)
          if (typeof raw === "string" && !raw.startsWith("[") && !raw.startsWith("{")) return;

          const data = JSON.parse(raw);
          if (Array.isArray(data)) {
            data.forEach((update: OrderBookUpdate) => {
              const bestBid = update.bids?.[0]
                ? parseFloat(update.bids[0].price)
                : 0;
              const bestAsk = update.asks?.[0]
                ? parseFloat(update.asks[0].price)
                : 0;

              // Call all registered callbacks for this asset
              callbacksRef.current.forEach((callback) => {
                callback(update.asset_id, bestBid, bestAsk);
              });
            });
          }
        } catch (e) {
          // Silently ignore parse errors from non-data messages
        }
      };

      ws.onerror = () => {
        // Error details not useful in browser WS events
      };

      ws.onclose = () => {
        setConnected(false);
        reconnectTimerRef.current = setTimeout(connect, 3000);
      };

      wsRef.current = ws;
    } catch {
      reconnectTimerRef.current = setTimeout(connect, 3000);
    }
  }, []);

  const subscribe = useCallback(
    (tokenIds: string[], callback: PriceCallback) => {
      // Add to tracked tokens
      tokenIds.forEach((id) => subscribedTokensRef.current.add(id));

      // Store callback
      const callbackId = Math.random().toString(36).slice(2);
      callbacksRef.current.set(callbackId, callback);

      // Send subscription if connected
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            assets_ids: tokenIds,
            type: "market",
            custom_feature_enabled: true,
          })
        );
      }

      // Return unsubscribe function
      return () => {
        callbacksRef.current.delete(callbackId);
      };
    },
    []
  );

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }
    wsRef.current?.close();
    wsRef.current = null;
    setConnected(false);
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    connected,
    subscribe,
    disconnect,
  };
}

// Singleton instance for shared WebSocket connection
let sharedWsInstance: {
  ws: WebSocket | null;
  callbacks: Map<string, Set<PriceCallback>>;
  tokens: Set<string>;
  reconnectTimer: NodeJS.Timeout | null;
  isConnecting: boolean;
  reconnectAttempts: number;
  pendingSubscriptions: Set<string>;
} | null = null;

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 5000;
const RECONNECT_RESET_DELAY = 60000; // Reset attempts after 60s cooldown
const MAX_TOKENS_PER_SUBSCRIPTION = 50; // Limit batch size

export function getSharedClobWs() {
  if (!sharedWsInstance) {
    sharedWsInstance = {
      ws: null,
      callbacks: new Map(),
      tokens: new Set(),
      reconnectTimer: null,
      isConnecting: false,
      reconnectAttempts: 0,
      pendingSubscriptions: new Set(),
    };
  }
  return sharedWsInstance;
}

export function connectSharedClobWs() {
  const instance = getSharedClobWs();

  // Don't connect if already open or connecting
  if (instance.ws?.readyState === WebSocket.OPEN) return;
  if (instance.ws?.readyState === WebSocket.CONNECTING) return;
  if (instance.isConnecting) return;

  // Stop if too many reconnect attempts, but schedule a reset so it recovers later
  if (instance.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    setTimeout(() => {
      const inst = getSharedClobWs();
      inst.reconnectAttempts = 0;
      // Retry if there are still active subscriptions
      if (inst.callbacks.size > 0) {
        connectSharedClobWs();
      }
    }, RECONNECT_RESET_DELAY);
    return;
  }

  instance.isConnecting = true;

  try {
    const ws = new WebSocket(CLOB_WS_URL);

    ws.onopen = () => {
      instance.isConnecting = false;
      instance.reconnectAttempts = 0;

      // Resubscribe in batches to avoid overwhelming the connection
      if (instance.tokens.size > 0) {
        const allTokens = Array.from(instance.tokens);
        for (let i = 0; i < allTokens.length; i += MAX_TOKENS_PER_SUBSCRIPTION) {
          const batch = allTokens.slice(i, i + MAX_TOKENS_PER_SUBSCRIPTION);
          ws.send(
            JSON.stringify({
              assets_ids: batch,
              type: "market",
              custom_feature_enabled: true,
            })
          );
        }
      }
    };

    ws.onmessage = (event) => {
      try {
        const raw = event.data;
        // Ignore non-JSON messages (PONG, INVALID OPERATION, etc.)
        if (typeof raw === "string" && !raw.startsWith("[") && !raw.startsWith("{")) return;

        const data = JSON.parse(raw);

        // Format 1: book snapshot — array of OrderBookUpdate
        if (Array.isArray(data)) {
          data.forEach((update: OrderBookUpdate) => {
            const bestBid = update.bids?.[0]
              ? parseFloat(update.bids[0].price)
              : 0;
            const bestAsk = update.asks?.[0]
              ? parseFloat(update.asks[0].price)
              : 0;

            const callbacks = instance.callbacks.get(update.asset_id);
            callbacks?.forEach((cb) => cb(update.asset_id, bestBid, bestAsk));
          });
        }
        // Format 2: price_changes — object with price_changes array
        else if (data.price_changes && Array.isArray(data.price_changes)) {
          for (const change of data.price_changes) {
            const assetId = change.asset_id;
            const bestBid = change.best_bid ? parseFloat(change.best_bid) : 0;
            const bestAsk = change.best_ask ? parseFloat(change.best_ask) : 0;
            if (assetId && (bestBid > 0 || bestAsk > 0)) {
              const callbacks = instance.callbacks.get(assetId);
              callbacks?.forEach((cb) => cb(assetId, bestBid, bestAsk));
            }
          }
        }
        // Format 3: best_bid_ask event
        else if (data.event_type === "best_bid_ask" && data.asset_id) {
          const bestBid = data.best_bid ? parseFloat(data.best_bid) : 0;
          const bestAsk = data.best_ask ? parseFloat(data.best_ask) : 0;
          if (bestBid > 0 || bestAsk > 0) {
            const callbacks = instance.callbacks.get(data.asset_id);
            callbacks?.forEach((cb) => cb(data.asset_id, bestBid, bestAsk));
          }
        }
      } catch (e) {
        // Silently ignore parse errors from non-data messages
      }
    };

    ws.onerror = () => {
      instance.isConnecting = false;
    };

    ws.onclose = () => {
      instance.isConnecting = false;
      instance.ws = null;

      // Only reconnect if we have active subscriptions and haven't exceeded attempts
      if (instance.callbacks.size > 0 && instance.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        instance.reconnectAttempts++;
        instance.reconnectTimer = setTimeout(connectSharedClobWs, RECONNECT_DELAY);
      }
    };

    instance.ws = ws;
  } catch {
    instance.isConnecting = false;
    instance.reconnectAttempts++;

    if (instance.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      instance.reconnectTimer = setTimeout(connectSharedClobWs, RECONNECT_DELAY);
    }
  }
}

export function subscribeToToken(tokenId: string, callback: PriceCallback) {
  const instance = getSharedClobWs();

  // Don't subscribe if we've exceeded reconnect attempts (WS is disabled)
  if (instance.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    return () => {}; // Return empty unsubscribe
  }

  instance.tokens.add(tokenId);

  if (!instance.callbacks.has(tokenId)) {
    instance.callbacks.set(tokenId, new Set());
  }
  instance.callbacks.get(tokenId)!.add(callback);

  // Subscribe if connected, otherwise connect (which will subscribe all tokens on open)
  if (instance.ws?.readyState === WebSocket.OPEN) {
    instance.ws.send(
      JSON.stringify({
        assets_ids: [tokenId],
        type: "market",
        custom_feature_enabled: true,
      })
    );
  } else if (!instance.isConnecting) {
    // Only try to connect if not already connecting
    connectSharedClobWs();
  }

  // Return unsubscribe
  return () => {
    instance.callbacks.get(tokenId)?.delete(callback);
    if (instance.callbacks.get(tokenId)?.size === 0) {
      instance.callbacks.delete(tokenId);
      instance.tokens.delete(tokenId);
    }
  };
}

/**
 * Hook for live prices of a binary market (2 outcomes)
 * Returns [price0, price1] where prices are updated via WebSocket
 * Uses midPrice (bestBid + bestAsk) / 2 — matches what Polymarket shows as "% chance".
 * Falls back to bestAsk when bestBid is 0 (illiquid orderbook).
 * Returns null for prices that haven't been received yet (falls back to static)
 */
export function useLivePrices(tokenIds: string[] | undefined): (number | null)[] {
  const [prices, setPrices] = useState<(number | null)[]>([null, null]);
  // Latest midpoint per token, written on every WS tick but flushed to state on
  // a throttle. The orderbook midpoint can tick several times/second on a live
  // match, and rendering each one made the price text flicker ("se mueve muy
  // rápido"). We keep the freshest value here and commit it ~every 600ms.
  const latestRef = useRef<(number | null)[]>([null, null]);

  useEffect(() => {
    if (!tokenIds || tokenIds.length < 2) return;

    const token0 = tokenIds[0];
    const token1 = tokenIds[1];

    // Reset prices when tokens change
    latestRef.current = [null, null];
    setPrices([null, null]);

    // Subscribe to both tokens — use midPrice to match Polymarket's displayed "% chance"
    // Fall back to bestAsk if bestBid is 0 (thin orderbook). Writes are ref-only.
    const unsub0 = subscribeToToken(token0, (assetId, bestBid, bestAsk) => {
      if (assetId === token0) {
        latestRef.current[0] = (bestBid > 0 && bestAsk > 0) ? (bestBid + bestAsk) / 2 : bestAsk;
      }
    });

    const unsub1 = subscribeToToken(token1, (assetId, bestBid, bestAsk) => {
      if (assetId === token1) {
        latestRef.current[1] = (bestBid > 0 && bestAsk > 0) ? (bestBid + bestAsk) / 2 : bestAsk;
      }
    });

    // Throttled flush: commit the freshest midpoints to state at most ~every
    // 600ms, only when they actually changed. Smooths the flicker without a
    // perceptible data lag on a moneyline price.
    const flush = setInterval(() => {
      setPrices((prev) => {
        const [a, b] = latestRef.current;
        return (a === prev[0] && b === prev[1]) ? prev : [a, b];
      });
    }, 600);

    return () => {
      unsub0();
      unsub1();
      clearInterval(flush);
    };
  }, [tokenIds?.[0], tokenIds?.[1]]);

  return prices;
}
